// Resoconto per periodo (GET /stats/period). I campi restano snake_case come li manda il
// backend: e' un aggregato di sola lettura, non c'e' niente da rimappare.

export type StatsPeriod = "30d" | "3m" | "6m" | "12m";

export const STATS_PERIODS: StatsPeriod[] = ["30d", "3m", "6m", "12m"];

// 'all' = tutti i clienti (solo operatori). Altrimenti l'id Keycloak del cliente.
export type StatsScopeUserId = "all" | string;

export type StatsLanguage = "it" | "en";

// Il resoconto conta solo i contenuti per cui abbiamo INVIATO una richiesta di rimozione.
// Eventi del periodo, ciascuno alla propria data: signaled (primo invio), removed /
// removed_declared / deindexed / neutralized (data del risultato). Fotografia alla data del
// resoconto: in_removal, unverifiable, rejected, not_removed, *_total_to_date, neutralized_rate
// (complessivo). Contano anche le richieste del sistema precedente (removal_requests).
export type StatsTotals = {
  signaled: number;
  removed: number;
  // esiti dichiarati dal sito ma non ancora verificati da noi (non sono neutralizzati)
  removed_declared: number;
  deindexed: number;
  neutralized: number;
  in_removal: number;
  // Ancora online ma non verificabili in automatico (social dietro login, sonda bloccata):
  // "in verifica manuale". Opzionale: assente nelle risposte dei backend precedenti.
  unverifiable?: number;
  // ancora online e l'ultima risposta del sito e' un rifiuto
  rejected: number;
  // Ancora online e l'ultima nostra richiesta e' di oltre 90 giorni fa: "non rimossi".
  // Restano nel denominatore del tasso. Opzionale: assente nei backend precedenti.
  not_removed?: number;
  signaled_total_to_date: number;
  neutralized_total_to_date: number;
  // 0..1, neutralized_total_to_date / signaled_total_to_date
  neutralized_rate: number;
  // Solo operatori (il backend non li manda al cliente):
  // 0..1, neutralizzati / (segnalati - non verificabili)
  neutralized_rate_verifiable?: number;
  // rimossi o fuori dai motori nel periodo SENZA una nostra richiesta (fuori dai numeri)
  neutralized_without_request?: number;
  avg_days_to_neutralize: number | null;
  notices_sent: number;
  reminders_sent: number;
  deindex_requests: number;
  deindex_requests_urls: number;
  practices_opened: number;
  searches: number;
};

// Situazione di tutti i contenuti segnalati fino a `at` (= to).
export type StatsStatusSnapshot = {
  removed: number;
  deindexed: number;
  removed_declared: number;
  in_removal: number;
  unverifiable?: number;
  rejected: number;
  not_removed?: number;
  total: number;
  at: string;
};

export type StatsTimelineBucket = {
  start: string; // YYYY-MM-DD
  signaled: number; // per data del primo invio
  neutralized: number; // per data del risultato
};

export type StatsTopDomain = {
  domain: string;
  signaled: number;
  neutralized: number; // removed + deindexed nel periodo
  // Opzionali: assenti nelle risposte dei backend precedenti.
  removed?: number;
  deindexed?: number;
  in_removal: number;
  unverifiable?: number;
  rejected?: number;
  not_removed?: number;
};

// Nello scope "tutti" il cliente e' l'intestatario della pratica se c'e' (customer), altrimenti
// l'account proprietario (user_id): esattamente uno dei due e' valorizzato. Serve per
// selezionare lo scope giusto al click.
export type StatsTopClient = {
  user_id: string | null;
  customer: string | null;
  name: string;
  signaled: number;
  neutralized: number; // removed + deindexed nel periodo
  // Opzionali: assenti nelle risposte dei backend precedenti.
  removed?: number;
  deindexed?: number;
  in_removal: number;
  unverifiable?: number;
  rejected?: number;
  not_removed?: number;
};

export type StatsRecentNeutralized = {
  url: string;
  domain: string;
  title: string;
  status: string;
  date: string;
};

export type PeriodStats = {
  period: StatsPeriod;
  from: string;
  to: string;
  bucket: "week" | "month";
  scope: { user_id: string | null; customer?: string | null; label: string };
  totals: StatsTotals;
  status_snapshot: StatsStatusSnapshot;
  timeline: StatsTimelineBucket[];
  top_domains: StatsTopDomain[];
  top_clients?: StatsTopClient[];
  recent_neutralized: StatsRecentNeutralized[];
  generated_at: string;
};

export type PeriodStatsParams = {
  period: StatsPeriod;
  // assente = il backend usa l'identita' della richiesta (cliente, o cliente delegato)
  userId?: StatsScopeUserId;
  // intestatario delle pratiche (legal_practices.customer_name), alternativo a userId: le
  // pratiche aperte da un operatore per conto del cliente hanno l'operatore come proprietario.
  customer?: string;
};

// GET /stats/clients (solo operatori): opzioni del selettore cliente.
export type StatsClientAccount = { user_id: string; name: string; email: string };
export type StatsClientCustomer = {
  name: string;
  practices: number;
  last_practice_at: string | null;
};
export type StatsClients = {
  accounts: StatsClientAccount[];
  customers: StatsClientCustomer[];
};
